/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { unzip } from 'fflate';

const EMAIL_BOUNDARY = 'BOUNDARY123';
// const EMAIL_DOWNLOAD_NAME = 'email.eml';

type EmailAttachment = {
  base64: string;
  contentType: string;
  fileName: string;
};

const CONTENT_TYPE_BY_EXTENSION: Record<string, string> = {
  '7z': 'application/x-7z-compressed',
  csv: 'text/csv',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  eml: 'message/rfc822',
  gif: 'image/gif',
  htm: 'text/html',
  html: 'text/html',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  json: 'application/json',
  pdf: 'application/pdf',
  png: 'image/png',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  rar: 'application/vnd.rar',
  rtf: 'application/rtf',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  txt: 'text/plain',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xml: 'application/xml',
  zip: 'application/zip',
};

const uint8ToBase64 = (bytes: Uint8Array): string => {
  const chunkSize = 0x8000;
  let binary = '';

  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    const chunk = bytes.subarray(offset, offset + chunkSize);
    let chunkBinary = '';
    for (let index = 0; index < chunk.length; index += 1) {
      chunkBinary += String.fromCharCode(chunk[index]);
    }
    binary += chunkBinary;
  }

  return btoa(binary);
};

const blobToBase64 = async (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string | null;
      if (!result) {
        reject(new Error('Unable to convert blob to Base64 string'));
        return;
      }
      const commaIndex = result.indexOf(',');
      resolve(commaIndex !== -1 ? result.substring(commaIndex + 1) : result);
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error('Unknown FileReader error'));
    };
    reader.readAsDataURL(blob);
  });

const guessContentType = (fileName: string): string => {
  const extension = fileName.split('.').pop()?.toLowerCase() ?? '';
  if (!extension) {
    return 'application/octet-stream';
  }
  return CONTENT_TYPE_BY_EXTENSION[extension] ?? 'application/octet-stream';
};

const wrapBase64 = (base64: string): string =>
  base64.match(/.{1,76}/g)?.join('\r\n') || base64;

const DOC_LABEL_MAPPINGS = [
  { label: 'DriversLicense', tokens: ['driver', 'dl', 'license'] },
  { label: 'EAD', tokens: ['ead', 'opt', 'h4', 'l2', 'gc'] },
  { label: 'GreenCardCopy', tokens: ['green_card', 'gc'] },
  { label: 'H1Copy', tokens: ['h1', 'h-1'] },
  { label: 'I94', tokens: ['i94', 'i-94'] },
  { label: 'PassportCopy', tokens: ['passport'] },
  { label: 'TravelHistory', tokens: ['travel'] },
  { label: 'Resume', tokens: ['resume'] },
  { label: 'OverviewSummary', tokens: ['overview'] },
];

const formatToken = (token: string) =>
  token
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');

const inferDescriptorLabel = (baseName: string) => {
  const normalized = baseName.toLowerCase().replace(/[\s-]+/g, '_');
  for (const mapping of DOC_LABEL_MAPPINGS) {
    if (mapping.tokens.some((token) => normalized.includes(token))) {
      return mapping.label;
    }
  }
  const tokens = normalized
    .split('_')
    .filter((token) => token && !/^\d+$/.test(token));
  if (!tokens.length) {
    return 'Attachment';
  }
  return tokens.map(formatToken).join('');
};

const sanitizeFileName = (rawFileName: string): string => {
  const safeValue = rawFileName?.trim();
  if (!safeValue) {
    return 'attachment';
  }

  const pathSegments = safeValue
    .split('/')
    .filter((segment) => segment && segment !== '.' && segment !== '..');

  if (!pathSegments.length) {
    return 'attachment';
  }

  const candidateName = pathSegments[0];
  const fileName = pathSegments[pathSegments.length - 1];
  const extensionMatch = fileName.match(/\.[^.]+$/);
  const extension = extensionMatch ? extensionMatch[0] : '';
  const baseName = fileName.replace(/\.[^.]+$/, '');

  const descriptorFormatted = inferDescriptorLabel(baseName);

  const combinedName =
    `${candidateName}_${descriptorFormatted}${extension}`.replace(/\s+/g, '_');

  try {
    return decodeURIComponent(combinedName);
  } catch (error) {
    console.warn('Unable to decode file name', rawFileName, error);
    return combinedName;
  }
};

const triggerEmlDownload = (emlContent: string, jobData: any) => {
  const date = new Date();
  const formattedDate = `${String(date.getMonth() + 1).padStart(
    2,
    '0',
  )}_${String(date.getDate()).padStart(2, '0')}`;
  const emlBlob = new Blob([emlContent], { type: 'message/rfc822' });
  const url = URL.createObjectURL(emlBlob);
  const link = document.createElement('a');
  link.href = url;
  const fileName = `Candidates_${jobData?.jobTitle}_${formattedDate}`;
  let downloadFileName = fileName?.replaceAll(' ', '_');
  // Ensure .eml extension is always present
  if (!downloadFileName?.toLowerCase().endsWith('.eml')) {
    downloadFileName = `${downloadFileName}.eml`;
  }
  link.download = downloadFileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

const buildEmlContent = ({
  attachments,
  emailBodyHtml,
  subject,
  from,
  to,
  cc,
  jobId,
  messageId,
  references,
}: {
  attachments: EmailAttachment[];
  emailBodyHtml: string;
  subject: string;
  from: string;
  to: string;
  cc: string;
  jobId: string;
  messageId: string;
  references: string[];
}): string => {
  const baseLines = [
    // `From: ${from}`,
    `To: ${from}`,
    // `Reply-To: ${from}`,
    // `Message-ID: ${jobId}`,
    // `In-Reply-To: ${messageId}`,
    // `References:${[references]}`,
    `Cc: ${cc}`,
    `Subject: ${subject}`,
    'MIME-Version: 1.0',
    'X-Unsent: 1',
    `Content-Type: multipart/mixed; boundary="${EMAIL_BOUNDARY}"`,
    '',
    `--${EMAIL_BOUNDARY}`,
    'Content-Type: text/html; charset=UTF-8',
    'Content-Transfer-Encoding: 7bit',
    '',
    emailBodyHtml,
    '',
  ];

  const attachmentSections = attachments.flatMap((attachment) => [
    `--${EMAIL_BOUNDARY}`,
    `Content-Type: ${attachment.contentType}; name="${attachment.fileName}"`,
    'Content-Transfer-Encoding: base64',
    `Content-Disposition: attachment; filename="${attachment.fileName}"`,
    '',
    wrapBase64(attachment.base64),
    '',
  ]);

  return [...baseLines, ...attachmentSections, `--${EMAIL_BOUNDARY}--`].join(
    '\r\n',
  );
};

const unzipArrayBuffer = async (
  arrayBuffer: ArrayBuffer,
): Promise<Record<string, Uint8Array>> =>
  new Promise((resolve, reject) => {
    unzip(new Uint8Array(arrayBuffer), (error, data) => {
      if (error || !data) {
        reject(error ?? new Error('Unable to unzip archive'));
        return;
      }
      resolve(data);
    });
  });

export const downloadEmailWithAttachments = async ({
  attachmentUrl,
  emailBodyHtml,
  fallbackFileName,
  subject,
  from,
  to,
  cc,
  messageId,
  references,
  jobId,
  jobData,
}: {
  attachmentUrl: string;
  emailBodyHtml: string;
  fallbackFileName: string;
  subject: string;
  from: string;
  to: string;
  cc: string;
  jobId: string;
  messageId: string;
  references: string[];
  jobData?: any;
}) => {
  const response = await fetch(attachmentUrl, { method: 'GET', mode: 'cors' });
  if (!response.ok) {
    throw new Error(
      `Unable to download attachment. Status: ${response.status}`,
    );
  }

  const blob = await response.blob();
  const contentType =
    blob.type ||
    response.headers.get('content-type') ||
    'application/octet-stream';

  const isZipFile =
    contentType.toLowerCase().includes('zip') ||
    attachmentUrl.toLowerCase().endsWith('.zip');

  let attachments: EmailAttachment[] = [];

  if (isZipFile) {
    const arrayBuffer = await blob.arrayBuffer();
    const filesMap = await unzipArrayBuffer(arrayBuffer);

    attachments = Object.entries(filesMap)
      .filter(([path]) => path && !path.endsWith('/'))
      .map(([path, content]) => {
        const fileName = sanitizeFileName(path);
        return {
          base64: uint8ToBase64(content),
          contentType: guessContentType(fileName),
          fileName,
        };
      });

    if (!attachments.length) {
      throw new Error('No files found inside the ZIP archive');
    }
  } else {
    const attachmentBase64 = await blobToBase64(blob);
    attachments = [
      {
        base64: attachmentBase64,
        contentType,
        fileName: fallbackFileName,
      },
    ];
  }

  const emlContent = buildEmlContent({
    attachments,
    cc,
    emailBodyHtml,
    from,
    jobId,
    messageId,
    references,
    subject,
    to,
  });

  triggerEmlDownload(emlContent, jobData);
};
